import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, PaginatedResponse } from '@synergy/api';
import { Dispatch } from 'redux';
import { DocumentStatus, DocumentType, ReceiptAlert } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { downloadFile } from '@synergy/utils';
import { paloIsRefused } from '@synergy/components-palo';
import { uploadFilesSet } from '@synergy/data-access';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { Inquiry } from './models/inquiry';
import { InquiryRequest } from './models/inquiry-request';
import { LockedPartner, noticeLockedPartners } from '../../common/utils/locked-partner-notice';
import { resourceMissing } from '../../common/reducers/resource-missing-reducer';

export const REQUEST_INQUIRIES = 'REQUEST_INQUIRIES';
export const RECEIVE_INQUIRIES = 'RECEIVE_INQUIRIES';
export const RESET_INQUIRIES_STATE = 'RESET_INQUIRIES_STATE';
export const REQUEST_INQUIRY = 'REQUEST_INQUIRY';
export const RECEIVE_INQUIRY = 'RECEIVE_INQUIRY';
export const DELETE_INQUIRY = 'DELETE_INQUIRY';
export const RESET_INQUIRY_STATE = 'RESET_INQUIRY_STATE';
export const CREATE_INQUIRY = 'CREATE_INQUIRY';
export const UPDATE_INQUIRY = 'UPDATE_INQUIRY';
export const DUPLICATE_INQUIRY = 'DUPLICATE_INQUIRY';
export const REQUEST_INQUIRY_START = 'REQUEST_INQUIRY_START';
export const REQUEST_INQUIRY_END = 'REQUEST_INQUIRY_END';
export const UPDATE_INQUIRY_ALERT = 'UPDATE_INQUIRY_ALERT';
export const RATE_SUCCESS = 'RATE_SUCCESS';

export const requestStart = () => {
    return {
        type: REQUEST_INQUIRY_START,
    };
};

export const requestEnd = () => {
    return {
        type: REQUEST_INQUIRY_END,
    };
};

export const requestInquiries = () => {
    return {
        type: REQUEST_INQUIRIES,
    };
};

export const resetInquiriesState = () => {
    return {
        type: RESET_INQUIRIES_STATE,
    };
};

export const receiveInquiries = (inquiries: PaginatedResponse<Inquiry>) => {
    return {
        type: RECEIVE_INQUIRIES,
        inquiries,
    };
};

export const requestInquiry = () => {
    return {
        type: REQUEST_INQUIRY,
    };
};

export const createInquiry = () => {
    return {
        type: CREATE_INQUIRY,
    };
};

export const updateInquiry = () => {
    return {
        type: UPDATE_INQUIRY,
    };
};

export const deleteInquiryAction = (inquiryId: number) => {
    return {
        type: DELETE_INQUIRY,
        inquiryId,
    };
};

export const resetInquiryState = () => {
    return {
        type: RESET_INQUIRY_STATE,
    };
};

export const receiveInquiry = (entity: Inquiry) => {
    return {
        type: RECEIVE_INQUIRY,
        entity,
    };
};

const inquiryAlertUpdated = (alert?: ReceiptAlert) => {
    return {
        type: UPDATE_INQUIRY_ALERT,
        alert,
    };
};

export const rateSuccess = (rating: number) => {
    return {
        type: RATE_SUCCESS,
        rating,
    };
};

export const fetchInquiries = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestStart());
        dispatch(requestInquiries());

        const response = await api.inquiries.cget(searchParams);
        if (response.success) {
            dispatch(receiveInquiries(response.result));
            dispatch(requestEnd());
        } else {
            dispatch(requestEnd());
            // The list says a refused filter in its own body, with the cause
            // and a way out; the toast would say it again without either.
            if (!paloIsRefused(searchParams.toString())) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};

export const fetchInquiry = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestInquiry());

        const response = await api.inquiries.get(id);
        if (response.success) {
            dispatch(receiveInquiry(response.result));
            // The files as the server has them, only when it sends the
            // document: set from every new copy of it, an upload just made
            // was overwritten by the files it was loaded with (R-193, R-194).
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else if (404 === response.errorCode) {
            // Not a fault to report in passing: the address names a record
            // that does not exist, and the mask says so instead of standing empty.
            dispatch(requestEnd());
            dispatch(resourceMissing());
        } else {
            dispatch(requestEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const duplicateInquiry = (id: number, partners: (LockedPartner | undefined)[] = []) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DUPLICATE_INQUIRY });

        const response = await api.inquiries.copyInquiry(id);
        dispatch(requestEnd());

        if (response.success) {
            noticeLockedPartners(dispatch, partners);
            dispatch(receiveInquiry(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const finishInquiry = (inquiry: Inquiry) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(updateInquiry());
        const response = await api.inquiries.patch(inquiry.id, { ...inquiry, status: DocumentStatus.Completed });

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.inquiryFinished')));
            dispatch(receiveInquiry(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.inquirySavedError')));
        }
    };
};

export const createOrUpdateInquiry = (inquiry: Partial<Inquiry>, inquiryId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        let response: ApiResult<Inquiry>;

        const { classifications, ...inquiryRequestBase } = inquiry;
        const classificationIds = classifications?.map(classification => classification.id) ?? [];
        const request: InquiryRequest = {
            ...inquiryRequestBase,
            classificationIds,
        };

        if (inquiryId) {
            dispatch(updateInquiry());
            response = await api.inquiries.patch(inquiryId, request);
        } else {
            dispatch(createInquiry());
            response = await api.inquiries.post(request);
        }

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.inquirySavedSuccess')));
            dispatch(receiveInquiry(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.inquirySavedError')));
        }
    };
};

export const updateReceiptAlert = (id: number, alert: ReceiptAlert) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.updateAlert(DocumentType.Inquiry, id, alert);
        const isNew = !getState().inquiries.entity?.receiptAlert;
        if (response.success) {
            dispatch(inquiryAlertUpdated(alert));
            dispatch(
                Snackbar.enqueue(
                    PoolNotification.success(
                        isNew ? 'components.receiptAlert.snackbar.added' : 'components.receiptAlert.snackbar.changed'
                    )
                )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteReceiptAlert = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.deleteAlert(DocumentType.Inquiry, id);
        if (response.success) {
            dispatch(inquiryAlertUpdated());
            dispatch(Snackbar.enqueue(PoolNotification.success('components.receiptAlert.snackbar.deleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteInquiry = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(deleteInquiryAction(id));
        const response = await api.inquiries.delete(id);
        if (response.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const exportInquiries = (filename: string, searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.inquiries.export(searchParams);
        if (response.success) {
            downloadFile(response.result, filename);
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const rate = (inquiryId: number, rating: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.inquiries.rate(inquiryId, rating);

        if (response.success) {
            dispatch(rateSuccess(rating));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
